import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell } from "@/components/PageShell";
import { PromiseBand } from "@/components/PromiseBand";
import { HealicMascot } from "@/components/HealicMascot";
import { HeroBookingForm } from "@/components/HeroBookingForm";
import { specialties, surgeries, contact, locations, treatmentSlug } from "@/lib/healic-data";
import { getTreatmentClinicalContent } from "@/lib/condition-content";
import { absUrl, BRAND, breadcrumbJsonLd, specialtySeo } from "@/lib/seo";
import {
  ArrowRight,
  Check,
  Phone,
  ShieldCheck,
  Star,
  Award,
  Lock,
  ChevronDown,
  Stethoscope,
  Scissors,
  PlusCircle,
  Timer,
  Wallet,
  ShieldPlus,
  ClipboardCheck,
  Quote,
  Thermometer,
  CircleDot,
  Frown,
  Zap,
  Moon,
  Droplets,
  Dna,
  Utensils,
  HeartPulse,
  Hourglass,
  CigaretteOff,
  AlertCircle,
} from "lucide-react";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { DoctorPortrait } from "@/components/DoctorPortrait";

import heroGS from "@/assets/specialty-general-surgery.jpg";
import heroDelivery from "@/assets/category-hero/lap-gyne.jpg.asset.json";
import heroCosmeticGyn from "@/assets/category-hero/cosmetic-gynae-banner.jpg.asset.json";
import heroOrtho from "@/assets/category-hero/orthopaedics-banner.png.asset.json";
import heroUro from "@/assets/category-hero/urology-banner.jpg.asset.json";
import heroEnt from "@/assets/category-hero/ent-banner.png.asset.json";
import heroEye from "@/assets/category-hero/ophthalmology.jpeg.asset.json";

import journey35 from "@/assets/journey/journey-35.jpeg.asset.json";
import journey34 from "@/assets/journey/journey-34.jpeg.asset.json";
import journey33 from "@/assets/journey/journey-33.jpeg.asset.json";
import journey32 from "@/assets/journey/journey-32.jpeg.asset.json";
import journey31 from "@/assets/journey/journey-31.jpeg.asset.json";

export type SpecialtyLoaderData = { specialty: (typeof specialties)[number] };

export const Route = createFileRoute("/specialties/$slug")({
  loader: ({ params }): SpecialtyLoaderData => {
    const specialty = specialties.find((s) => s.slug === params.slug);
    if (!specialty) throw notFound();
    return { specialty };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.specialty;
    if (!s) return { meta: [{ title: "Specialty — Healic" }] };
    const path = `/specialties/${s.slug}`;
    const url = absUrl(path);
    const seo = specialtySeo({ name: s.name, slug: s.slug, short: s.short, description: s.description });
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: `${seo.shortName} — ${BRAND}` },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${seo.shortName} — Healic` },
        { name: "twitter:description", content: seo.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalSpecialty",
            name: s.name,
            description: s.description,
            relevantSpecialty: s.treatments,
            url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: genericFaqs(s.name).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Specialties", url: absUrl("/specialties") },
          { name: seo.shortName, url },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Specialty not found</h1>
        <Link to="/specialties" className="mt-6 inline-block text-accent">Browse all specialties →</Link>
      </div>
    </PageShell>
  ),
  component: () => <SpecialtyPage data={Route.useLoaderData()} />,
});

const heroBySlug: Record<string, string> = {
  "general-surgery": heroGS,
  "gynaecology": heroDelivery.url,
  "cosmetic-gynaecology": heroCosmeticGyn.url,
  "orthopaedics": heroOrtho.url,
  "urology": heroUro.url,
  "ent": heroEnt.url,
  "ophthalmology": heroEye.url,
};

const heroTaglineBySlug: Record<string, string> = {
  "general-surgery": "Get Rid of Hernia, Piles & Gallstones",
  "gynaecology": "Safe Deliveries & Women's Surgery",
  "cosmetic-gynaecology": "Confidence, Comfort & Intimate Wellness",
  "orthopaedics": "Walk Pain-Free Again",
  "urology": "End Kidney Stone & Prostate Pain",
  "ent": "Breathe, Hear & Sleep Better",
  "ophthalmology": "See the World Clearly Again",
};

const insurancePartners = [
  "ICICI Lombard",
  "Bajaj Allianz",
  "Niva Bupa",
  "Care Health",
  "Star Health",
  "HDFC ERGO",
];

function genericFaqs(name: string) {
  return [
    { q: `Does insurance cover ${name.toLowerCase()} procedures at Healic?`, a: "Yes — most planned procedures are covered under cashless health insurance and Ayushman Bharat. Our insurance desk handles pre-authorisation paperwork for you." },
    { q: "How long does the procedure take?", a: "Most planned procedures take 30–90 minutes depending on the case. Your surgeon will share an exact estimate after consultation." },
    { q: "Do I have to stay in the hospital?", a: "Many procedures are daycare — admission and discharge on the same day. Larger surgeries may need 1–3 nights of observation." },
    { q: "How much time should I take off from work or school?", a: "Desk-job patients typically return in 2–5 days. Physically demanding work may need 1–2 weeks. Your surgeon will guide based on your case." },
    { q: "Are there any side effects of this treatment?", a: "Modern minimally invasive techniques have low complication rates. Mild soreness or swelling is normal and settles in a few days with prescribed medication." },
  ];
}

const benefitStrip = [
  { icon: Scissors, label: "Minimally Invasive Options" },
  { icon: PlusCircle, label: "Day-Care Procedure" },
  { icon: Timer, label: "Quick Recovery" },
  { icon: ShieldCheck, label: "Safe & Effective" },
  { icon: Lock, label: "Low Recurrence Risk" },
  { icon: Award, label: "Expert Surgeons" },
];

const symptomItems = [
  { icon: Thermometer, text: "Persistent pain or discomfort" },
  { icon: CircleDot, text: "Visible swelling or change in tissue" },
  { icon: Frown, text: "Difficulty performing daily activities" },
  { icon: Zap, text: "Sudden flare-ups or sharp episodes" },
  { icon: Moon, text: "Disturbed sleep or constant fatigue" },
  { icon: Droplets, text: "Unusual discharge, bleeding or fever" },
];

const causeItems = [
  { icon: Dna, tint: "from-rose-500/15 to-rose-500/0 text-rose-600", text: "Genetic predisposition" },
  { icon: Utensils, tint: "from-amber-500/15 to-amber-500/0 text-amber-600", text: "Lifestyle & diet factors" },
  { icon: HeartPulse, tint: "from-emerald-500/15 to-emerald-500/0 text-emerald-600", text: "Underlying conditions" },
  { icon: Hourglass, tint: "from-sky-500/15 to-sky-500/0 text-sky-600", text: "Age-related wear" },
  { icon: CigaretteOff, tint: "from-violet-500/15 to-violet-500/0 text-violet-600", text: "Smoking & environment" },
  { icon: AlertCircle, tint: "from-orange-500/15 to-orange-500/0 text-orange-600", text: "Past infections / injuries" },
];

function journeyStepsFor(_heroImg: string) {
  return [
    { title: "Proper Counselling", img: journey31.url },
    { title: "Fixed Price Explanation", img: journey32.url },
    { title: "Safe Surgery", img: journey33.url },
    { title: "Structured Monitoring", img: journey34.url },
    { title: "Time-Bound Discharge", img: journey35.url },
  ];
}

function defaultReviews(name: string) {
  return [
    { name: "Priya M.", text: `Very transparent pricing and caring staff. Highly recommend Healic for ${name.toLowerCase()}.` },
    { name: "Rahul S.", text: `Got my treatment done at Healic ${name}. The team was super supportive and recovery was fast.` },
    { name: "Anil K.", text: `Doctors explained everything patiently. Surgery was smooth and I was discharged on time.` },
    { name: "Sunita R.", text: `Excellent experience from consultation to discharge. The fixed-price package gave complete peace of mind.` },
  ];
}



function FaqItem({ q, a, defaultOpen = false, index }: { q: string; a: string; defaultOpen?: boolean; index: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <li className={`overflow-hidden rounded-xl border bg-card transition ${open ? "border-accent" : "border-border"}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left">
        <span className={`text-sm font-semibold ${open ? "text-accent" : "text-foreground"}`}>
          {index}. {q}
        </span>
        <ChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="border-t border-border px-5 py-4 text-sm text-muted-foreground">{a}</p>}
    </li>
  );
}

export function SpecialtyPage({ data }: { data: SpecialtyLoaderData }) {
  const { specialty } = data;
  const allDoctors = useMergedDoctors();
  const doctors = allDoctors
    .filter((d) => d.specialtySlug === specialty.slug)
    .sort((a, b) => (parseInt(String(b.experience ?? "").match(/\d+/)?.[0] ?? "0", 10)) - (parseInt(String(a.experience ?? "").match(/\d+/)?.[0] ?? "0", 10)));
  const tagline = heroTaglineBySlug[specialty.slug] ?? `Expert ${specialty.name} Care`;
  const heroImg = heroBySlug[specialty.slug] ?? heroGS;
  const faqs = genericFaqs(specialty.name);

  return (
    <PageShell>
      {/* Hero with bg image */}
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <img src={heroImg} alt="" aria-hidden fetchPriority="high" decoding="async" width={780} height={520} className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/40" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
          <Link to="/specialties" className="text-[11px] font-semibold uppercase tracking-widest text-accent hover:opacity-80">
            ← All specialties
          </Link>
          <div className="mt-3 grid items-start gap-6 lg:grid-cols-[1.35fr_1fr]">
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                {specialty.name}
              </h1>
              <p className="mt-3 max-w-xl text-sm opacity-90 lg:text-base">{specialty.description}</p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-accent-foreground hover:opacity-90">
                  Book consultation <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a href={`tel:${contact.phoneTel}`} className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-5 py-2.5 text-xs font-semibold ring-1 ring-white/25 backdrop-blur">
                  <Phone className="h-3.5 w-3.5 text-accent" /> {contact.phone}
                </a>
              </div>

              {/* Trust stats inside hero */}
              <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
                {[
                  { icon: Award, big: "5,000", small: "Successful Surgeries" },
                  { icon: Star, big: "Average 4.8", small: "Patient Rating" },
                  { icon: Stethoscope, big: "85+ NPS", small: "Happy Patients" },
                  { icon: ShieldCheck, big: "Insurance", small: "All Accepted" },
                ].map(({ icon: Icon, big, small }) => (
                  <div key={small} className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div>
                      <p className="font-display text-xs font-bold leading-tight sm:text-sm">{big}</p>
                      <p className="mt-0.5 text-[10px] leading-snug opacity-80">{small}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature badges — fills left column to match form height */}
              <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {[
                  { icon: Wallet, label: "0% EMI Available" },
                  { icon: ShieldPlus, label: "Cashless Insurance" },
                  { icon: Timer, label: "Single Point Customer Support" },
                  { icon: ClipboardCheck, label: "Free Consultation" },
                  { icon: Scissors, label: "Advanced Tech" },
                  { icon: PlusCircle, label: "Pickup & Drop" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/15 backdrop-blur">
                    <Icon className="h-4 w-4 shrink-0 text-accent" />
                    <span className="text-[11px] font-semibold leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <HeroBookingForm tagline={tagline} />
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE BAND */}
      <PromiseBand />


      {/* ABOUT — body + image */}
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-2 lg:px-8 lg:pb-14">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">About {specialty.name}</h2>
        </div>
        <div className="mt-8 grid items-center gap-8 lg:grid-cols-2">
          <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            <p>{specialty.description}</p>
            <p>
              At Healic, our {specialty.name.toLowerCase()} team uses modern, evidence-based techniques to deliver focused care for every patient. From the first consultation and diagnostics, through treatment, and into recovery and follow-up — you get a personalised plan with transparent pricing and dedicated Single Point of Customer support.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border shadow-soft">
            <img src={heroImg} alt={specialty.name} loading="lazy" width={800} height={600} className="aspect-[4/3] w-full object-cover" />
          </div>
        </div>

      </section>

      {/* TREATMENTS & PROCEDURES */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-12">
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">Treatments &amp; Procedures</h2>
          <ul className="mx-auto mt-8 grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {specialty.treatments.map((t: string) => {
              const tSlug = treatmentSlug(t);
              const hasTreatmentPage = !!getTreatmentClinicalContent(tSlug);
              const linked = !hasTreatmentPage
                ? surgeries.find(
                    (s) => s.specialtySlug === specialty.slug && (s.name === t || s.name.toLowerCase().includes(t.toLowerCase().split(" ")[0])),
                  )
                : undefined;
              const content = (
                <>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-semibold text-accent">{t}</span>
                </>
              );
              const cls = "flex items-center gap-3 rounded-pill border border-border bg-card px-5 py-3 shadow-soft transition hover:border-accent hover:shadow-card";
              return (
                <li key={t}>
                  {linked ? (
                    <Link to="/surgeries/$slug" params={{ slug: linked.slug }} className={cls}>{content}</Link>
                  ) : (
                    <Link to="/treatments/$slug" params={{ slug: tSlug }} className={cls}>{content}</Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* WHY CHOOSE HEALIC — 5-step journey */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">
              Why Choose Healic for<br />{specialty.name}
            </h2>
          </div>
          <div className="mt-10 rounded-3xl bg-primary-soft/70 px-4 py-8 sm:px-8">
            <ul className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
              {journeyStepsFor(heroImg).map((step, i, arr) => (
                <li key={step.title} className="relative flex flex-col items-center text-center">
                  <span className="absolute -top-2 left-1/2 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-card text-[12px] font-bold text-foreground shadow-soft">
                    {i + 1}
                  </span>
                  <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
                    <img src={step.img} alt={step.title} loading="lazy" width={320} height={224} className="h-24 w-full object-cover sm:h-28" />
                  </div>
                  <p className="mt-3 font-display text-[13px] font-bold leading-tight text-foreground">{step.title}</p>
                  {i < arr.length - 1 && (
                    <ArrowRight className="absolute right-[-18px] top-12 hidden h-5 w-5 text-accent lg:block" />
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground shadow-soft">
            <div className="grid divide-y divide-white/15 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">500+</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">Surgeries Every Month</p>
              </div>
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">Average 4.8</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">Google Rating</p>
              </div>
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">85+</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">NPS Score</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATIENT STORIES */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Patient Stories</span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">What Our Patients say</h2>
          </div>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {defaultReviews(specialty.name).map((r) => (
              <li key={r.name} className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft">
                <Quote className="h-5 w-5 text-accent/40" />
                <div className="mt-2 flex items-center gap-1 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-foreground/80">"{r.text}"</p>
                <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 font-display text-xs font-semibold text-primary">
                    {r.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">Verified patient</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DOCTORS WHO PERFORM */}
      {doctors.length > 0 && (
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
            <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
              Our {specialty.name} Specialists
            </h2>
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {doctors.map((d) => {
                const loc = locations.find((l) => l.slug === d.locationSlug);
                const profileSlug = doctorProfileSlug(d.slug, d.name);
                return (
                  <li key={d.slug} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
                    <DoctorPortrait doctor={d} className="aspect-[4/3] overflow-hidden" imgClassName="object-top" />
                    <div className="flex flex-1 flex-col p-4">
                      <p className="font-display text-base font-bold leading-tight text-foreground">{d.name}</p>
                      <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">{d.specialty}</p>
                      {d.qualifications && (
                        <p className="mt-1 line-clamp-1 text-[12px] font-semibold text-foreground/80">{d.qualifications}</p>
                      )}
                      {d.experience && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">{d.experience} experience</p>
                      )}
                      {loc && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">Healic {loc.city}</p>
                      )}
                      <Link
                        to="/doctors/$slug"
                        params={{ slug: profileSlug }}
                        className="mt-3 block rounded-pill bg-primary px-3 py-2 text-center text-[12px] font-semibold uppercase tracking-wide text-primary-foreground hover:opacity-90"
                      >
                        Consult Now
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}


      {/* FAQs — polished card with mascot badge */}
      <section className="bg-gradient-to-b from-background via-primary-soft/30 to-background">
        <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
          <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <div className="grid lg:grid-cols-[0.9fr_1.4fr]">
              {/* Left: heading + mascot on tinted panel */}
              <div className="relative flex flex-col items-center justify-between gap-6 bg-primary-soft/60 p-6 text-center lg:p-8">
                <div>
                  <span className="inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
                    FAQ
                  </span>
                  <h2 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
                    Find Answers to<br />Your Questions
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    We are happy to answer every question you have.
                  </p>
                </div>
                <div className="relative flex w-full items-end justify-center">
                  <div className="absolute bottom-0 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-accent/20 blur-2xl" />
                  <HealicMascot size="lg" className="relative !h-56 !w-56 sm:!h-64 sm:!w-64 lg:!h-72 lg:!w-72" />
                </div>
              </div>

              {/* Right: FAQ list */}
              <div className="p-5 sm:p-7 lg:p-8">
                <ul className="space-y-3">
                  {faqs.map((f, i) => (
                    <FaqItem key={f.q} q={f.q} a={f.a} index={i + 1} defaultOpen={i === 0} />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

